import React, { useState } from "react";
import { ArrowRight, ChevronDown, ChevronRight, CopyCheck, Replace } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { fmtDate, fmtINR } from "../lib/api";

/**
 * Phase 138 — the conflict review that sits under the Import Payments preview table.
 *   • "Conflicts with existing entries": file rows that match an existing payment of the same PIN with a different
 *     amount (same receipt) or a different receipt no. (same date + amount). Existing vs imported side by side.
 *     Ticked rows REPLACE the existing entry once the admin confirms in the confirm bar.
 *   • "Already recorded – skipped": rows whose receipt + amount already exist for the PIN (locked, nothing happens).
 * Shared by the main Import Payments tab and the Third Party payments tab.
 */
const SHOW_STEP = 25;

const DIFF_LABELS = { amount: "Amount", receipt: "Receipt no.", date: "Date" };

export const ENTERED_LABELS = { app: "App entry", import: "Bulk import", citizen_portal: "Citizen portal" };

function Side({ receipt, date, amount, mode, who, via, differences = [], side, testId }) {
  const hi = (k) => (differences.includes(k) ? (side === "existing" ? "text-destructive line-through decoration-destructive/60" : "text-amber-800 font-semibold") : "");
  return (
    <div className="space-y-0.5 min-w-[150px]" data-testid={testId}>
      <p className={`font-mono text-xs ${hi("receipt")}`}>{receipt || "—"}</p>
      <p className={`text-xs tabular-nums ${hi("amount")}`}>{amount != null ? fmtINR(amount) : "—"}<span className="text-muted-foreground font-normal"> · {mode || "—"}</span></p>
      <p className={`text-[11px] text-muted-foreground ${hi("date")}`}>{date ? fmtDate(date) : "—"}</p>
      {(who || via) && (
        <p className="text-[11px] text-muted-foreground truncate max-w-[200px]">{who || ENTERED_LABELS[via] || via}{who && via ? ` · ${ENTERED_LABELS[via] || via}` : ""}</p>
      )}
    </div>
  );
}

export function ImportConflictsPanel({ rows, selected, onToggle, tid = "payment-import" }) {
  const conflicts = rows.filter((r) => r.action === "replace" && r.status !== "error");
  const already = rows.filter((r) => r.action === "already_recorded");
  const [shown, setShown] = useState(SHOW_STEP);
  const [openAlready, setOpenAlready] = useState(false);
  if (conflicts.length === 0 && already.length === 0) return null;
  const ticked = conflicts.filter((r) => selected.has(r.row_number)).length;
  const existingTotal = conflicts.reduce((n, r) => n + (r.conflict?.existing?.amount || 0), 0);
  const importedTotal = conflicts.reduce((n, r) => n + (r.amount || 0), 0);

  return (
    <div className="space-y-3" data-testid={`${tid}-conflicts`}>
      {conflicts.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/40 shadow-sm" data-testid={`${tid}-conflicts-card`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2 text-amber-900">
              <Replace className="h-4 w-4" aria-hidden="true" />
              Conflicts with existing entries
              <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-300 tabular-nums" data-testid={`${tid}-conflicts-count`}>{conflicts.length}</Badge>
              <span className="ml-auto text-xs font-normal text-amber-900/80 tabular-nums" data-testid={`${tid}-conflicts-ticked`}>{ticked} of {conflicts.length} ticked to replace</span>
            </CardTitle>
            <p className="text-xs text-amber-900/80">
              Each row below matches a payment already recorded for the same PIN but with a <b>different amount</b> (same receipt no.) or a
              <b> different receipt no.</b> (same date and amount). Ticked rows <b>replace</b> the existing entry with the imported data when you
              confirm: the old record is kept as <i>Replaced by import</i> for audit, drops out of every total and report, and the imported
              payment is credited to the same staff member. Untick a row to leave the existing entry as it is.
            </p>
            <p className="text-[11px] text-amber-900/70 tabular-nums" data-testid={`${tid}-conflicts-totals`}>
              Existing {fmtINR(existingTotal)} → imported {fmtINR(importedTotal)}
              {" "}({importedTotal - existingTotal >= 0 ? "+" : "−"}{fmtINR(Math.abs(importedTotal - existingTotal))} net change once all are replaced)
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="border-t border-amber-200 overflow-x-auto">
              <Table data-testid={`${tid}-conflicts-table`}>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10" />
                    <TableHead className="w-14">Row</TableHead>
                    <TableHead>PIN</TableHead>
                    <TableHead>Existing entry</TableHead>
                    <TableHead className="w-8" />
                    <TableHead>Imported (file)</TableHead>
                    <TableHead>What differs</TableHead>
                    <TableHead>Credited to</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conflicts.slice(0, shown).map((r) => {
                    const ex = r.conflict?.existing || {};
                    const on = selected.has(r.row_number);
                    const diffs = r.conflict?.differences || [];
                    return (
                      <TableRow key={r.row_number} className={on ? "bg-amber-100/40" : "opacity-70"} data-testid={`${tid}-conflict-row-${r.row_number}`} data-selected={on ? "true" : "false"}>
                        <TableCell className="align-top pt-3">
                          <Checkbox checked={on} onCheckedChange={() => onToggle(r.row_number)} aria-label={`Replace existing entry for row ${r.row_number}`}
                            data-testid={`${tid}-conflict-check-${r.row_number}`} />
                        </TableCell>
                        <TableCell className="font-mono text-xs align-top pt-3">{r.row_number}</TableCell>
                        <TableCell className="align-top pt-3">
                          <p className="font-mono text-xs">{r.pin}</p>
                          {r.owner_name && <p className="text-[11px] text-muted-foreground truncate max-w-[140px]">{r.owner_name}</p>}
                        </TableCell>
                        <TableCell className="align-top pt-3">
                          <Side side="existing" receipt={ex.receipt_display} date={ex.payment_date} amount={ex.amount} mode={ex.payment_mode}
                            who={ex.collected_by_name} via={ex.entered_via} differences={diffs} testId={`${tid}-conflict-existing-${r.row_number}`} />
                        </TableCell>
                        <TableCell className="align-top pt-4 text-amber-700"><ArrowRight className="h-4 w-4" aria-hidden="true" /></TableCell>
                        <TableCell className="align-top pt-3">
                          <Side side="imported" receipt={r.receipt} date={r.payment_date} amount={r.amount} mode={r.payment_mode}
                            differences={diffs} testId={`${tid}-conflict-imported-${r.row_number}`} />
                        </TableCell>
                        <TableCell className="align-top pt-3">
                          <div className="flex flex-wrap gap-1" data-testid={`${tid}-conflict-diffs-${r.row_number}`}>
                            {diffs.map((d) => (
                              <Badge key={d} variant="outline" className="text-[10px] font-normal bg-amber-100 text-amber-900 border-amber-300">{DIFF_LABELS[d] || d}</Badge>
                            ))}
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">{r.conflict?.label}</p>
                        </TableCell>
                        <TableCell className="align-top pt-3 text-xs" data-testid={`${tid}-conflict-credit-${r.row_number}`}>
                          {r.credited_to_name ? (
                            <>
                              <p className="font-medium">{r.credited_to_name}{r.credited_to_employee_id ? <span className="text-muted-foreground font-normal"> ({r.credited_to_employee_id})</span> : null}</p>
                              <p className="text-[10px] text-muted-foreground">{r.credited_via === "replaced_entry" ? "same as the replaced entry" : "assigned staff"}</p>
                            </>
                          ) : <Badge variant="outline" className="text-[10px] font-normal bg-violet-50 text-violet-800 border-violet-200">Imported (Legacy)</Badge>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {conflicts.length > shown && (
              <div className="p-2 text-center border-t border-amber-200">
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setShown(shown + SHOW_STEP)} data-testid={`${tid}-conflicts-more`}>
                  Show {Math.min(SHOW_STEP, conflicts.length - shown)} more of {conflicts.length - shown} remaining
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {already.length > 0 && (
        <Collapsible open={openAlready} onOpenChange={setOpenAlready}>
          <div className="rounded-lg border bg-muted/30" data-testid={`${tid}-already-card`}>
            <CollapsibleTrigger asChild>
              <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`${tid}-already-toggle`}>
                {openAlready ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                <CopyCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="font-medium">Already recorded – skipped</span>
                <Badge variant="secondary" className="tabular-nums" data-testid={`${tid}-already-count`}>{already.length}</Badge>
                <span className="text-xs text-muted-foreground ml-1">same PIN, receipt no. and amount already exist — nothing is changed for these rows</span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border-t overflow-x-auto">
                <Table data-testid={`${tid}-already-table`}>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-14">Row</TableHead>
                      <TableHead>PIN</TableHead>
                      <TableHead>Receipt</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Date in file</TableHead>
                      <TableHead>Existing record</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {already.slice(0, 100).map((r) => {
                      const ex = r.existing || {};
                      return (
                        <TableRow key={r.row_number} data-testid={`${tid}-already-row-${r.row_number}`}>
                          <TableCell className="font-mono text-xs">{r.row_number}</TableCell>
                          <TableCell className="font-mono text-xs">{r.pin}</TableCell>
                          <TableCell className="font-mono text-xs">{r.receipt}</TableCell>
                          <TableCell className="text-right tabular-nums text-xs">{fmtINR(r.amount)}</TableCell>
                          <TableCell className="text-xs">{r.payment_date ? fmtDate(r.payment_date) : r.date_raw || "—"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {ex.payment_date ? fmtDate(ex.payment_date) : "—"} · {ex.collected_by_name || ENTERED_LABELS[ex.entered_via] || "recorded"}
                            {ex.payment_date && r.payment_date && ex.payment_date !== r.payment_date && <span className="text-amber-700"> · date differs</span>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {already.length > 100 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground">…and {already.length - 100} more (see the Status filter in the table above)</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}
    </div>
  );
}

export default ImportConflictsPanel;
